import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Texto de célula que corta com "…" e revela o conteúdo inteiro no hover.
 *
 * A dica usa o `title` nativo, escrito direto no nó no `pointerenter` — sem
 * estado, sem re-render, sem trocar a árvore. Duas alternativas foram
 * descartadas:
 *
 * - Montar um `Tooltip` do Radix ao entrar o ponteiro: trocar o `<span>` por
 *   `<Tooltip><TooltipTrigger asChild>` faz o React desmontar o span e montar
 *   outro. O novo nasce debaixo do cursor sem nunca ter recebido
 *   `pointerenter`, então o `pointerleave` que deveria fechá-lo não vem — e
 *   cada célula por onde o mouse passa deixa uma dica presa na tela.
 * - Manter um `Tooltip` montado por célula: seriam milhares de roots do Radix
 *   numa tabela de 500 linhas, com a rolagem travando de forma visível.
 *
 * O `title` nativo não tem esses dois problemas: quem mostra e esconde é o
 * navegador, e não existe estado para ficar preso.
 *
 * Requer célula com largura definida (`table-layout: fixed`) e
 * `overflow-hidden` — sem isso a coluna cresce e nada nunca "estoura".
 *
 * Dentro de um flex, use `min-w-0` e NÃO `flex-1`. `flex-1` traz
 * `flex-basis: 0`, e com `min-width: 0` junto a contribuição de largura do
 * texto para o pai vira zero. Onde o pai é dimensionado pelo conteúdo — todo
 * `<button>` e todo `inline-flex`, o que inclui a casca dos editores inline —
 * ele encolhe até o ícone e o texto sai cortado com a coluna meio vazia.
 * `min-w-0` sozinho (`flex: 0 1 auto`) cresce até o conteúdo e só encolhe
 * quando falta espaço de verdade, que é o comportamento desejado.
 */
export function CellText({
  children,
  title,
  className,
}: {
  children: React.ReactNode;
  /** Texto da dica. Sem ele, usa o texto renderizado da própria célula. */
  title?: string;
  className?: string;
}) {
  return (
    <span
      onPointerEnter={(e) => {
        const el = e.currentTarget;
        // Só anuncia o que está escondido: repetir um texto que já cabe na
        // tela vira ruído. O +1 absorve o arredondamento de subpixel, que
        // marcaria tudo como estourado.
        if (el.scrollWidth > el.clientWidth + 1) {
          el.title = title ?? el.textContent ?? "";
        } else {
          el.removeAttribute("title");
        }
      }}
      className={cn("block truncate", className)}
    >
      {children}
    </span>
  );
}
